import { useState, useEffect } from 'react'
import { moderationApi, type QueueReport, type ModerationDashboard, type MyReport } from '../../api/moderation'
import { useUiStore } from '../../stores/uiStore'
import { Spinner } from '../ui/Spinner'

interface Props {
  onBack: () => void
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-bg-input rounded-xl p-3 text-center">
      <p className="text-txt-primary font-bold text-xl">{value}</p>
      <p className="text-txt-meta text-xs mt-0.5">{label}</p>
    </div>
  )
}

function MyReportsTab() {
  const { addToast } = useUiStore()
  const [reports, setReports] = useState<MyReport[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    moderationApi.listMyReports()
      .then((r) => setReports(r.reports ?? []))
      .catch(() => addToast('error', 'Не удалось загрузить жалобы'))
      .finally(() => setLoading(false))
  }, [addToast])

  const STATUS_STYLE: Record<string, string> = {
    pending: 'text-yellow-400 bg-yellow-400/10',
    resolved: 'text-green-400 bg-green-400/10',
    dismissed: 'text-txt-meta bg-bg-hover',
  }

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>

  if (reports.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2">
        <span className="text-3xl">📋</span>
        <p className="text-txt-primary font-medium">No reports submitted</p>
        <p className="text-txt-meta text-sm">You haven't reported anything yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {reports.map((r) => (
        <div key={r.id} className="bg-bg-input rounded-xl p-3 space-y-1.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-txt-primary text-sm font-medium capitalize">{r.reason}</p>
              <p className="text-txt-meta text-xs">{r.target_type} · {new Date(r.created_at).toLocaleDateString()}</p>
              {r.description && <p className="text-txt-secondary text-xs mt-0.5 line-clamp-2">{r.description}</p>}
              {r.action_taken && <p className="text-txt-meta text-xs mt-1 italic">Action: {r.action_taken}</p>}
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${STATUS_STYLE[r.status] ?? STATUS_STYLE.pending}`}>
              {r.status}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

export function ModerationQueuePanel({ onBack }: Props) {
  const { addToast } = useUiStore()
  const [tab, setTab] = useState<'queue' | 'my-reports'>('queue')
  const [reports, setReports] = useState<QueueReport[]>([])
  const [dashboard, setDashboard] = useState<ModerationDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [actioning, setActioning] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([moderationApi.getQueue(), moderationApi.getDashboard()])
      .then(([q, d]) => {
        setReports(q.reports ?? [])
        setDashboard(d)
      })
      .catch(() => addToast('error', 'Не удалось загрузить очередь'))
      .finally(() => setLoading(false))
  }, [addToast])

  const handleResolve = async (report: QueueReport) => {
    setActioning(report.id)
    try {
      await moderationApi.resolveReport(report.id, 'reviewed')
      setReports((prev) => prev.filter((r) => r.id !== report.id))
      addToast('success', 'Жалоба решена')
    } catch {
      addToast('error', 'Не удалось решить жалобу')
    } finally {
      setActioning(null)
    }
  }

  const handleDismiss = async (report: QueueReport) => {
    setActioning(report.id)
    try {
      await moderationApi.dismissReport(report.id)
      setReports((prev) => prev.filter((r) => r.id !== report.id))
      addToast('success', 'Жалоба отклонена')
    } catch {
      addToast('error', 'Не удалось отклонить жалобу')
    } finally {
      setActioning(null)
    }
  }

  const handleBanUser = async (report: QueueReport) => {
    setActioning(report.id + '-ban')
    try {
      await moderationApi.globalBanUser(report.reporter_id, report.reason)
      addToast('success', 'Пользователь заблокирован')
    } catch {
      addToast('error', 'Не удалось заблокировать пользователя')
    } finally {
      setActioning(null)
    }
  }

  const handleWarnUser = async (report: QueueReport) => {
    setActioning(report.id + '-warn')
    try {
      await moderationApi.warnUser(report.reporter_id, report.reason)
      addToast('success', 'Предупреждение отправлено')
    } catch {
      addToast('error', 'Не удалось отправить предупреждение')
    } finally {
      setActioning(null)
    }
  }

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors" aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">🛡️ Moderation</h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-2 border-b border-divider shrink-0">
        {(['queue', 'my-reports'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors
              ${tab === t ? 'bg-accent text-white' : 'text-txt-secondary hover:text-txt-primary hover:bg-bg-hover'}`}
          >
            {t === 'queue' ? 'Queue' : 'My Reports'}
          </button>
        ))}
      </div>

      {tab === 'my-reports' ? (
        <div className="flex-1 overflow-y-auto p-4">
          <MyReportsTab />
        </div>
      ) : loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {dashboard && (
            <div className="p-4 border-b border-divider">
              <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium mb-3">Dashboard</p>
              <div className="grid grid-cols-2 gap-2">
                <StatCard label="Pending" value={dashboard.pending_reports} />
                <StatCard label="Resolved today" value={dashboard.resolved_today} />
                <StatCard label="Active bans" value={dashboard.active_bans} />
                <StatCard label="Total reports" value={dashboard.total_reports} />
              </div>
            </div>
          )}

          <div className="p-4">
            <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium mb-3">
              Pending Reports ({reports.length})
            </p>
            {reports.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <span className="text-3xl">✅</span>
                <p className="text-txt-primary font-medium">Queue is empty</p>
                <p className="text-txt-meta text-sm">No pending reports</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reports.map((report) => (
                  <div key={report.id} className="bg-bg-input rounded-xl p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-txt-primary text-sm font-medium capitalize">{report.reason}</p>
                        <p className="text-txt-meta text-xs mt-0.5">
                          {report.target_type} · {new Date(report.created_at).toLocaleDateString()}
                        </p>
                        {report.description && (
                          <p className="text-txt-secondary text-xs mt-1 line-clamp-2">{report.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        onClick={() => handleResolve(report)}
                        disabled={!!actioning}
                        className="text-xs py-1.5 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors disabled:opacity-50"
                      >
                        ✓ Resolve
                      </button>
                      <button
                        onClick={() => handleDismiss(report)}
                        disabled={!!actioning}
                        className="text-xs py-1.5 rounded-lg bg-txt-meta/10 text-txt-meta hover:bg-txt-meta/20 transition-colors disabled:opacity-50"
                      >
                        ✕ Dismiss
                      </button>
                      <button
                        onClick={() => handleWarnUser(report)}
                        disabled={!!actioning}
                        className="text-xs py-1.5 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 transition-colors disabled:opacity-50"
                      >
                        ⚠ Warn
                      </button>
                      <button
                        onClick={() => handleBanUser(report)}
                        disabled={!!actioning}
                        className="text-xs py-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50"
                      >
                        🚫 Ban
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
